import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HubLayout from '../components/common/HubLayout';
import FamilyContextBanner from '../components/common/FamilyContextBanner';
import FamilyContentToggle from '../components/common/FamilyContentToggle';
import FamilyBadge from '../components/common/FamilyBadge';
import { useFamily } from '../contexts/FamilyContext';
import { useDailyQuestion } from '../hooks/useDailyQuestion';
import { axiosInstance } from '../services/api';
import { getUser } from '../utils/secureStorage';
import WisdomMomentsFeed from '../components/videoactivation/WisdomMomentsFeed';
import SpeakerNotificationBanner from '../components/videoactivation/SpeakerNotificationBanner';
import { getPendingConfirmations } from '../services/videoActivationAPI';
import '../styles/hub-chapters.css';

// --- Type Configuration ---
const TYPE_CONFIG = {
  story: { icon: "\u{1F4D6}", label: "Story", plural: "Stories", bg: "#E8F5E9", border: "#A5D6A7", text: "#2E7D32", accent: "#556B2F" },
  milestone: { icon: "\u{1F3C6}", label: "Big Moment", plural: "Your Big Moments", bg: "#FFF8E1", border: "#FFD54F", text: "#F57F17", accent: "#E6A700" },
  capsule: { icon: "\u{1F4EC}", label: "Capsule", plural: "Time Capsules", bg: "#E8EAF6", border: "#9FA8DA", text: "#283593", accent: "#3949AB" },
  log: { icon: "\u{1F4CB}", label: "Log", plural: "Quick Logs", bg: "#eaf0f6", border: "#b0c4d8", text: "#334455", accent: "#4a6178" },
};

const FONT_HEADING = "'Lora', Georgia, serif";
const FONT_BODY = "'Source Sans 3', sans-serif";

// --- SVG Icons ---
const SearchIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
  </svg>
);
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);
const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
  </svg>
);
const HeartIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#E65100" : "none"} stroke={filled ? "#E65100" : "currentColor"} strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// --- Story Thumbnail Decorations ---
const THUMB_GRADIENTS = [
  "linear-gradient(135deg, #8B4513 0%, #A0522D 50%, #6B3410 100%)",
  "linear-gradient(135deg, #C8D6B9 0%, #A8BF94 100%)",
  "linear-gradient(135deg, #7B68AE 0%, #9B8EC4 50%, #5C4D8A 100%)",
  "linear-gradient(135deg, #2E6BA6 0%, #4A8BC2 50%, #1A4F80 100%)",
  "linear-gradient(135deg, #4A7C59 0%, #6B9E7A 50%, #2E5A3A 100%)",
  "linear-gradient(135deg, #C4A030 0%, #E6C445 50%, #9A7E20 100%)",
  "linear-gradient(135deg, #5A7FA0 0%, #7A9FBF 50%, #3A5F80 100%)",
  "linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #8B6914 100%)",
  "linear-gradient(135deg, #2E7D32 0%, #4CAF50 50%, #1B5E20 100%)",
  "linear-gradient(135deg, #DDD5C4 0%, #C4B9A4 100%)",
];
const THUMB_EMOJIS = ["\u{1F4D6}", "\u{1F4DD}", "\u{1F4AD}", "\u{1F399}\uFE0F", "\u{1F3AC}", "\u{1F3E1}", "\u2728", "\u{1F31F}", "\u2615", "\u{1F331}"];

// --- Additional Icons for My Stories ---
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const HeartFilledIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="#E65100" stroke="#E65100" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const CommentIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// --- Milestone Timeline Helpers ---
const MILESTONE_CATEGORY_ICONS = {
  graduation: '🎓', wedding: '💒', baby: '👶', new_home: '🏠', new_job: '💼',
  retirement: '🎉', military: '🎖️', travel: '✈️', custom: '⭐',
  marriage: '💍', career: '💼', home: '🏠', birth: '👶', loss: '🕯️',
  achievement: '🏆', education: '🎓', family: '👨‍👩‍👧', other: '⭐',
};
const MILESTONE_STATUS_COLORS = {
  'Coming up':       { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B', border: '#FDE68A' },
  'Lived \u2713':    { bg: '#DCFCE7', text: '#166534', dot: '#22C55E', border: '#BBF7D0' },
  'Capsule Linked':  { bg: '#DCFCE7', text: '#166534', dot: '#22C55E', border: '#BBF7D0' },
};

const MilestoneTimelineCard = ({ milestone, isHovered, onMouseEnter, onMouseLeave, onClick }) => {
  const sc = MILESTONE_STATUS_COLORS[milestone.status] || MILESTONE_STATUS_COLORS['Lived \u2713'];
  const icon = MILESTONE_CATEGORY_ICONS[milestone.category] || '⭐';
  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        background: '#fff', borderRadius: 16, padding: '22px 24px', width: 280, cursor: 'pointer',
        boxShadow: isHovered ? '0 8px 28px rgba(85,107,47,0.18)' : '0 2px 10px rgba(0,0,0,0.07)',
        border: `1px solid ${isHovered ? '#A3BE6E' : '#E8EDE0'}`,
        transition: 'all 0.2s',
        transform: isHovered ? 'translateY(-3px)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, background: '#FEF3C7', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{icon}</div>
          <div>
            <div style={{ fontWeight: 700, color: '#2C3E1A', fontSize: 18 }}>{milestone.title}</div>
            <div style={{ color: '#888', fontSize: 13, fontFamily: FONT_BODY }}>{milestone.category}</div>
          </div>
        </div>
        <span style={{ background: sc.bg, color: sc.text, fontSize: 12, fontFamily: FONT_BODY, fontWeight: 600, padding: '4px 10px', borderRadius: 20, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap' }}>
          {milestone.status}
        </span>
      </div>
      {/* Child indicator */}
      {milestone.isMinor && milestone.personName && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FFF3E0', padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#E65100', fontFamily: FONT_BODY, marginBottom: 10 }}>
          👶 {milestone.personName}
        </div>
      )}
      {milestone.preview && <p style={{ margin: '0 0 14px', fontSize: 14, color: '#666', fontFamily: FONT_BODY, lineHeight: 1.6 }}>{milestone.preview}</p>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#888', fontFamily: FONT_BODY }}>📅 {milestone.month
          ? milestone.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : String(milestone.year || milestone.date.getFullYear())
        }</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {milestone.capsuleLinked && <span style={{ fontSize: 12, color: '#556B2F', fontFamily: FONT_BODY, background: '#EFF5E8', padding: '3px 10px', borderRadius: 10 }}>📦 Capsule</span>}
        </div>
      </div>
    </div>
  );
};

// --- Milestones Summary (mini timeline for dashboard) ---
const MilestoneSummaryTimeline = ({ items, totalCount, onViewAll, navigate }) => {
  const [hovered, setHovered] = useState(null);
  const displayItems = items.slice(0, 3);

  return (
    <div style={{
      background: '#fff', borderRadius: 20, border: '1px solid #E8E4DA',
      padding: 26, boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#2C3E1A', margin: 0, fontFamily: FONT_HEADING, display: 'flex', alignItems: 'center', gap: 10 }}>
          🏆 Your Big Moments
        </h2>
        <button onClick={onViewAll} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, color: '#556B2F',
          fontFamily: FONT_BODY, display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, transition: 'background 0.15s',
        }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#F0EDE4'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          View All {totalCount} <ArrowRight />
        </button>
      </div>

      {displayItems.length > 0 ? (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          {/* Mini spine */}
          <div style={{ position: 'absolute', left: 9, top: 8, bottom: 8, width: 2, background: 'linear-gradient(to bottom, #C8D5B0 0%, #8BAD5A 50%, #C8D5B0 100%)' }} />

          {displayItems.map((m, i) => {
            const sc = MILESTONE_STATUS_COLORS[m.status] || MILESTONE_STATUS_COLORS['Lived \u2713'];
            const icon = MILESTONE_CATEGORY_ICONS[m.category] || '⭐';
            const isHov = hovered === m.id;
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', borderRadius: 14, background: isHov ? '#F0EDE4' : '#F8F7F2',
                marginBottom: i < displayItems.length - 1 ? 8 : 0,
                border: `1px solid ${isHov ? '#A3BE6E' : '#EDEADF'}`, cursor: 'pointer',
                transition: 'all 0.15s', position: 'relative',
              }}
                onClick={() => navigate(`/my-life/milestones/${m.id}`)}
                onMouseEnter={() => setHovered(m.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Spine dot */}
                <div style={{
                  position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)',
                  width: 14, height: 14, borderRadius: '50%', background: sc.dot,
                  border: '2px solid #fff', boxShadow: `0 0 0 2px ${sc.dot}40`,
                  transition: 'transform 0.2s',
                  ...(isHov ? { transform: 'translateY(-50%) scale(1.3)' } : {}),
                }} />
                <div style={{ width: 40, height: 40, background: '#FEF3C7', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#2C3E1A', fontFamily: FONT_BODY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{m.title}</div>
                    {m.isMinor && m.personName && (
                      <span style={{ fontSize: 10, fontWeight: 600, background: '#FFF3E0', color: '#E65100', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: FONT_BODY }}>
                        👶 {m.personName.split(' ')[0]}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#999', fontFamily: FONT_BODY }}>
                    {m.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {m.capsuleLinked && <span style={{ marginLeft: 8, color: '#556B2F' }}>📦</span>}
                  </div>
                </div>
                <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontFamily: FONT_BODY, fontWeight: 600, padding: '3px 8px', borderRadius: 16, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {m.status}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 20px', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 10, opacity: 0.5 }}>🗺️</div>
          <p style={{ fontSize: 16, fontFamily: FONT_BODY, marginBottom: 4 }}>Your journey starts here</p>
          <p style={{ fontSize: 14, fontFamily: FONT_BODY, color: '#bbb' }}>Add your life's big moments</p>
        </div>
      )}
    </div>
  );
};

// --- Full Milestones Timeline Page (centered spine) ---
const MilestonesTimelinePage = ({ items, onBack, navigate, onDelete }) => {
  const [hovered, setHovered] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const filtered = items.filter((item) => {
    if (activeFilter !== 'All') {
      const filterDef = MILESTONE_FILTERS.find(f => f.key === activeFilter);
      if (filterDef && !filterDef.match(item)) return false;
    }
    if (search && !item.title.toLowerCase().includes(search.toLowerCase()) &&
      !(item.preview || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleDeleteClick = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm('Remove this moment? This cannot be undone.')) return;
    setDeletingId(item.id);
    try {
      if (onDelete) await onDelete(item.id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ background: '#FAF9F5' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '24px 28px 60px' }}>
        {/* Back button */}
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: 8, background: 'none',
          border: '1.5px solid #D4CFC4', borderRadius: 10, padding: '9px 18px', cursor: 'pointer',
          fontSize: 15, fontWeight: 600, color: '#556B2F', fontFamily: FONT_BODY, marginBottom: 22,
          transition: 'all 0.15s',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#556B2F'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = '#D4CFC4'; }}
        >
          <BackIcon /> Back to My Hub
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 52, height: 52, background: '#FEF3C7', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🏆</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 28, color: '#3A4D1F', fontWeight: 700, fontFamily: FONT_HEADING }}>Your Big Moments</h1>
              <p style={{ margin: 0, color: '#888', fontSize: 14, fontFamily: FONT_BODY }}>The turning points of your life journey</p>
            </div>
          </div>
          <button onClick={() => navigate('/my-life/add-milestone')} style={{
            background: '#E65100', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 24px',
            fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 3px 10px rgba(230,81,0,0.35)',
          }}>
            ＋ Add a Moment
          </button>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {MILESTONE_FILTERS.map(({ key, match }) => {
            const count = key === 'All' ? items.length : items.filter(match).length;
            return (
              <button key={key} onClick={() => { setActiveFilter(key); }}
                style={{
                  padding: '10px 20px', borderRadius: 30, border: 'none', cursor: 'pointer',
                  fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600,
                  background: activeFilter === key ? '#556B2F' : '#fff',
                  color: activeFilter === key ? '#fff' : '#666',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', transition: 'all 0.2s',
                }}
              >
                {key} ({count})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
          border: '2px solid #DDD8CE', borderRadius: 14, padding: '10px 16px', marginBottom: 28,
        }}>
          <SearchIcon size={18} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search moments..."
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 16, fontFamily: FONT_BODY, background: 'transparent', color: '#333' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              background: '#E8E4DA', border: 'none', borderRadius: '50%', width: 24, height: 24,
              cursor: 'pointer', fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{'\u2715'}</button>
          )}
        </div>

        {/* Timeline */}
        {filtered.length > 0 ? (
          <div style={{ position: 'relative' }}>
            {/* Center spine */}
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, background: 'linear-gradient(to bottom, #C8D5B0 0%, #8BAD5A 30%, #8BAD5A 70%, #C8D5B0 100%)', transform: 'translateX(-50%)', zIndex: 0 }} />

            {filtered.map((m, i) => {
              const isLeft = i % 2 === 0;
              const sc = MILESTONE_STATUS_COLORS[m.status] || MILESTONE_STATUS_COLORS['Lived \u2713'];
              const isHov = hovered === m.id;
              const isExpanded = expandedId === m.id;
              const isDeleting = deletingId === m.id;

              // Determine if we need a date separator
              const dateLabel = m.month
                ? m.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                : String(m.year || m.date.getFullYear());
              const prevItem = i > 0 ? filtered[i - 1] : null;
              const prevDateLabel = prevItem
                ? (prevItem.month
                    ? prevItem.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : String(prevItem.year || prevItem.date.getFullYear()))
                : null;
              const showDateMarker = i === 0 || dateLabel !== prevDateLabel;
              const displayDate = m.month
                ? m.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : String(m.year || m.date.getFullYear());

              const card = (
                <div style={{ opacity: isDeleting ? 0.5 : 1 }}>
                  <MilestoneTimelineCard
                    milestone={m}
                    isHovered={isHov}
                    onMouseEnter={() => {}}
                    onMouseLeave={() => {}}
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  />
                  {/* Action row on click */}
                  {isExpanded && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/my-life/milestones/${m.id}/edit`); }}
                        style={{ flex: 1, background: '#556B2F', color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY, padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}>
                        Edit
                      </button>
                      {!m.capsuleLinked && (
                        <button onClick={(e) => { e.stopPropagation(); navigate('/time-capsules/create', { state: { linkedMilestoneId: m.id } }); }}
                          style={{ flex: 1, background: '#fff', color: '#556B2F', fontSize: 14, fontFamily: FONT_BODY, padding: '10px 0', borderRadius: 10, border: '1.5px solid #556B2F', cursor: 'pointer' }}>
                          Write a Letter
                        </button>
                      )}
                      <button onClick={(e) => handleDeleteClick(e, m)}
                        style={{ padding: '10px 14px', background: '#fff', border: '1.5px solid #E8E4DA', borderRadius: 10, cursor: 'pointer', fontSize: 16, color: '#ccc', transition: 'color 0.15s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#D32F2F'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#ccc'}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              );

              return (
                <div key={m.id}>
                  {/* Date marker on the spine */}
                  {showDateMarker && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20, position: 'relative', zIndex: 3 }}>
                      <div style={{
                        background: '#3A4D1F', color: '#fff', fontSize: 13, fontWeight: 700,
                        fontFamily: FONT_BODY, padding: '6px 18px', borderRadius: 20,
                        boxShadow: '0 2px 8px rgba(58,77,31,0.25)', letterSpacing: 0.5,
                      }}>
                        {dateLabel}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 40, position: 'relative', cursor: 'pointer' }}
                    onMouseEnter={() => setHovered(m.id)} onMouseLeave={() => setHovered(null)}>

                    {/* Left slot */}
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', paddingRight: 36 }}>
                      {isLeft ? card : (
                        /* Date on the opposite side of the card */
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', paddingTop: 24 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#999', fontFamily: FONT_BODY, whiteSpace: 'nowrap' }}>
                            {displayDate}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Center dot */}
                    <div style={{ position: 'relative', zIndex: 2, flexShrink: 0, marginTop: 24 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', background: sc.dot,
                        border: '3px solid #fff', boxShadow: `0 0 0 3px ${sc.dot}40`,
                        transition: 'transform 0.2s', transform: isHov ? 'scale(1.35)' : 'scale(1)',
                      }} />
                    </div>

                    {/* Right slot */}
                    <div style={{ flex: 1, paddingLeft: 36 }}>
                      {!isLeft ? card : (
                        /* Date on the opposite side of the card */
                        <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: 24 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#999', fontFamily: FONT_BODY, whiteSpace: 'nowrap' }}>
                            {displayDate}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Spine end cap */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#C8D5B0', border: '2px solid #fff' }} />
            </div>
          </div>
        ) : (
          /* Empty state */
          <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🗺️</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#3A4D1F', fontFamily: FONT_HEADING, marginBottom: 8 }}>
              {search ? 'No moments match your search' : 'Your journey starts here'}
            </h2>
            <p style={{ fontSize: 16, color: '#666', fontFamily: FONT_BODY, lineHeight: 1.6, marginBottom: 24 }}>
              Big moments. Turning points. The chapters that shaped who you are.
              Add them so the people you love can understand your story.
            </p>
            {!search && (
              <button onClick={() => navigate('/my-life/add-milestone')}
                style={{ background: '#E65100', color: '#fff', fontSize: 16, fontWeight: 600, fontFamily: FONT_BODY, padding: '14px 28px', borderRadius: 12, border: 'none', cursor: 'pointer', boxShadow: '0 3px 10px rgba(230,81,0,0.35)' }}>
                ＋ Add Your First Moment
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes storyIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// --- Badges ---
const MediaBadge = ({ type }) => {
  if (!type) return null;
  const config = {
    video: { icon: "\u{1F3AC}", label: "Video", bg: "#EDE7F6", color: "#5E35B1" },
    audio: { icon: "\u{1F399}\uFE0F", label: "Audio", bg: "#E8F5E9", color: "#2E7D32" },
    image: { icon: "\u{1F5BC}\uFE0F", label: "Photo", bg: "#FFF3E0", color: "#E65100" },
  };
  const c = config[type] || config.video;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
      background: c.bg, color: c.color, fontFamily: FONT_BODY,
    }}>
      {c.icon} {c.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    shared: { label: "Shared", bg: "#E8F5E9", color: "#2E7D32", dot: "#4CAF50" },
    draft: { label: "Draft", bg: "#FFF8E1", color: "#F57F17", dot: "#FFC107" },
    private: { label: "Private", bg: "#F3E5F5", color: "#7B1FA2", dot: "#AB47BC" },
  };
  const c = config[status] || config.shared;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
      background: c.bg, color: c.color, fontFamily: FONT_BODY,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.dot }} />
      {c.label}
    </span>
  );
};

const FamilyEngagement = ({ lovedBy, commentedBy, loves, comments }) => {
  if ((!lovedBy || lovedBy.length === 0) && loves === 0 && comments === 0) {
    return (
      <span style={{ fontSize: 13, color: "#ccc", fontStyle: "italic", fontFamily: FONT_BODY }}>
        No family activity yet
      </span>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      {loves > 0 && (
        <span style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 13, color: "#E65100", fontWeight: 600, fontFamily: FONT_BODY,
        }}>
          <HeartFilledIcon /> {loves}
          {lovedBy && lovedBy.length > 0 && (
            <span style={{ color: "#999", fontWeight: 400 }}> by {lovedBy.join(", ")}</span>
          )}
        </span>
      )}
      {comments > 0 && (
        <span style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 13, color: "#666", fontFamily: FONT_BODY,
        }}>
          <CommentIcon /> {comments}
          {commentedBy && commentedBy.length > 0 && (
            <span style={{ color: "#999" }}> from {commentedBy.join(", ")}</span>
          )}
        </span>
      )}
    </div>
  );
};

// ==================== My Stories Section ====================
const SORT_OPTIONS = ["Newest First", "Oldest First", "Most Loved"];
const STATUS_FILTERS = ["All", "Shared", "Draft", "Private"];

const MyStoriesSection = ({ stories, onBack, navigate }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Newest First");
  const [statusFilter, setStatusFilter] = useState("All");
  const [hoveredId, setHoveredId] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const filtered = stories.filter((s) => {
    if (statusFilter !== "All" && s.status !== statusFilter.toLowerCase()) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase()) &&
      !(s.subtitle || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "Oldest First") return a.date - b.date;
    if (sortBy === "Most Loved") return b.loves - a.loves;
    return b.date - a.date;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const visible = sorted.slice((page - 1) * perPage, page * perPage);

  const sharedCount = stories.filter(s => s.status === "shared").length;
  const draftCount = stories.filter(s => s.status === "draft").length;
  const privateCount = stories.filter(s => s.status === "private").length;

  return (
    <div style={{ background: "#FAF9F5" }}>
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 28px 60px" }}>

        {/* Back button */}
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "none", border: "1.5px solid #D4CFC4", borderRadius: 10,
          padding: "9px 18px", cursor: "pointer", fontSize: 15, fontWeight: 600,
          color: "#556B2F", fontFamily: FONT_BODY,
          marginBottom: 22, transition: "all 0.15s",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#556B2F"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.borderColor = "#D4CFC4"; }}
        >
          <BackIcon /> Back to My Hub
        </button>

        {/* Title + New Story Button */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 24,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
              border: "1.5px solid #A5D6A7",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28,
            }}>{"\u{1F4D6}"}</div>
            <div>
              <h1 style={{
                fontSize: 30, fontWeight: 800, color: "#2C3E1A", margin: 0,
                fontFamily: FONT_HEADING,
              }}>
                My Stories
              </h1>
              <p style={{ fontSize: 15, color: "#999", margin: "2px 0 0", fontFamily: FONT_BODY }}>
                {stories.length} stories &middot; {sharedCount} shared &middot; {draftCount} draft{draftCount !== 1 ? "s" : ""} &middot; {privateCount} private
              </p>
            </div>
          </div>

          <button onClick={() => navigate('/tell-a-story')} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "12px 24px", borderRadius: 14, border: "none",
            background: "#556B2F", color: "#fff", cursor: "pointer",
            fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
            transition: "background 0.15s",
            boxShadow: "0 2px 8px rgba(85,107,47,0.25)",
          }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#6B8E23"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#556B2F"}
          >
            <PlusIcon /> New Story
          </button>
        </div>

        {/* Search & Sort */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, background: "#fff",
            border: "2px solid #DDD8CE", borderRadius: 14, padding: "12px 18px",
            flex: 1, minWidth: 260, transition: "border-color 0.2s",
          }}>
            <SearchIcon size={19} />
            <input
              type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search my stories..."
              style={{
                flex: 1, border: "none", outline: "none", fontSize: 17,
                fontFamily: FONT_BODY, background: "transparent", color: "#333",
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{
                background: "#E8E4DA", border: "none", borderRadius: "50%",
                width: 26, height: 26, cursor: "pointer", fontSize: 13, color: "#888",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{"\u2715"}</button>
            )}
          </div>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} style={{
            padding: "12px 16px", borderRadius: 12, border: "2px solid #DDD8CE",
            background: "#fff", fontSize: 16, fontFamily: FONT_BODY,
            color: "#556B2F", fontWeight: 600, cursor: "pointer",
          }}>
            {SORT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>

        {/* Status filter chips */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {STATUS_FILTERS.map((status) => {
            const count = status === "All" ? stories.length :
              stories.filter(s => s.status === status.toLowerCase()).length;
            return (
              <button
                key={status}
                onClick={() => { setStatusFilter(status); setPage(1); }}
                style={{
                  padding: "8px 18px", borderRadius: 24, border: "none", cursor: "pointer",
                  fontSize: 15, fontWeight: statusFilter === status ? 700 : 500,
                  fontFamily: FONT_BODY,
                  background: statusFilter === status ? "#556B2F" : "#EDEADF",
                  color: statusFilter === status ? "#fff" : "#666",
                  transition: "all 0.15s",
                }}
              >
                {status} ({count})
              </button>
            );
          })}
        </div>

        {/* Results count */}
        <p style={{ fontSize: 14, color: "#bbb", marginBottom: 14, fontFamily: FONT_BODY }}>
          Showing {visible.length} of {sorted.length} stories
        </p>

        {/* Story List */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((story, i) => {
            const isHovered = hoveredId === story.id;
            return (
              <div
                key={story.id}
                onClick={() => navigate(`/stories/${story.id}`)}
                onMouseEnter={() => setHoveredId(story.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: "flex", alignItems: "stretch", gap: 0,
                  background: "#fff", borderRadius: 16,
                  border: `1.5px solid ${isHovered ? "#A5D6A7" : "#E8E4DA"}`,
                  overflow: "hidden", cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isHovered ? "0 6px 20px rgba(85,107,47,0.08)" : "0 1px 4px rgba(0,0,0,0.02)",
                  transform: isHovered ? "translateY(-1px)" : "translateY(0)",
                  animation: `storyIn 0.35s ease ${i * 0.05}s both`,
                }}
              >
                {/* Green accent left border */}
                <div style={{
                  width: 5, flexShrink: 0,
                  background: isHovered
                    ? "linear-gradient(180deg, #556B2F 0%, #6B8E23 100%)"
                    : story.status === "draft"
                      ? "linear-gradient(180deg, #FFD54F 0%, #FFC107 100%)"
                      : story.status === "private"
                        ? "linear-gradient(180deg, #CE93D8 0%, #AB47BC 100%)"
                        : "linear-gradient(180deg, #A5D6A7 0%, #81C784 100%)",
                  transition: "background 0.25s",
                }} />

                {/* Thumbnail */}
                <div style={{
                  width: 72, height: 72, flexShrink: 0, margin: "14px 0 14px 16px",
                  borderRadius: 14, overflow: "hidden",
                  background: story.thumbnailUrl ? '#000' : story.thumbGradient,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 30, position: "relative",
                  boxShadow: "inset 0 2px 8px rgba(0,0,0,0.1)",
                  transition: "transform 0.2s",
                  transform: isHovered ? "scale(1.05)" : "scale(1)",
                }}>
                  {story.thumbnailUrl ? (
                    <>
                      {story.mediaType === 'video' ? (
                        <video
                          src={`${story.thumbnailUrl}#t=0.5`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          muted
                          playsInline
                          preload="auto"
                        />
                      ) : (
                        <img
                          src={story.thumbnailUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                      {story.mediaType === 'video' && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(0,0,0,0.3)',
                        }}>
                          <span style={{ fontSize: 20, color: '#fff' }}>▶️</span>
                        </div>
                      )}
                    </>
                  ) : story.thumbEmoji}
                </div>

                {/* Content */}
                <div style={{ flex: 1, padding: "14px 20px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                    <h3 style={{
                      fontSize: 18, fontWeight: 700, color: "#2C3E1A", margin: 0,
                      fontFamily: FONT_HEADING,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      flex: 1,
                    }}>
                      {story.title}
                    </h3>
                    <StatusBadge status={story.status} />
                    <MediaBadge type={story.mediaType} />
                    {story.hasPendingVoices && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/stories/${story.id}`);
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          background: '#FFF7F0', border: '1px solid #F0C070',
                          borderRadius: '20px', padding: '2px 10px',
                          fontSize: '11px', fontWeight: 600, color: '#E65100',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>🎤</span> Identify voice
                      </button>
                    )}
                  </div>
                  {story.subtitle && (
                    <p style={{
                      fontSize: 15, color: "#999", margin: "0 0 10px",
                      fontFamily: FONT_BODY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {story.subtitle}
                    </p>
                  )}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 10,
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 14,
                      fontSize: 14, fontFamily: FONT_BODY,
                    }}>
                      <span style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 4 }}>
                        {"\u{1F4C5}"} {story.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {story.location && (
                        <span style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 4 }}>
                          {"\u{1F4CD}"} {story.location}
                        </span>
                      )}
                    </div>
                    <FamilyEngagement
                      lovedBy={story.lovedBy}
                      commentedBy={story.commentedBy}
                      loves={story.loves}
                      comments={story.comments}
                    />
                  </div>
                </div>

                {/* Action buttons (visible on hover) */}
                <div style={{
                  display: "flex", flexDirection: "column", justifyContent: "center",
                  gap: 6, padding: "0 16px", flexShrink: 0,
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.2s",
                }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/stories/${story.id}`); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "7px 14px", borderRadius: 9, border: "1.5px solid #D4CFC4",
                      background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
                      color: "#556B2F", fontFamily: FONT_BODY,
                      transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#556B2F"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#556B2F"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#556B2F"; e.currentTarget.style.borderColor = "#D4CFC4"; }}
                  >
                    <EditIcon /> Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "7px 14px", borderRadius: 9, border: "1.5px solid #E8E4DA",
                      background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
                      color: "#999", fontFamily: FONT_BODY,
                      transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#FFF5F5"; e.currentTarget.style.color = "#D32F2F"; e.currentTarget.style.borderColor = "#FFCDD2"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#999"; e.currentTarget.style.borderColor = "#E8E4DA"; }}
                  >
                    <TrashIcon /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {visible.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: 52, marginBottom: 16, opacity: 0.5 }}>{"\u{1F50D}"}</div>
            <p style={{ fontSize: 18, color: "#999", fontFamily: FONT_BODY }}>
              No stories match your search.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, marginTop: 32,
          }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{
              padding: "11px 22px", borderRadius: 12, border: "2px solid #E8E4DA",
              background: page === 1 ? "#F5F3EE" : "#fff",
              cursor: page === 1 ? "default" : "pointer",
              fontSize: 16, fontWeight: 600, color: page === 1 ? "#ccc" : "#556B2F",
              fontFamily: FONT_BODY,
            }}>
              &larr; Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={{
                width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
                fontSize: 16, fontWeight: page === p ? 800 : 500,
                background: page === p ? "#556B2F" : "transparent",
                color: page === p ? "#fff" : "#777",
                fontFamily: FONT_BODY,
              }}>
                {p}
              </button>
            ))}
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{
              padding: "11px 22px", borderRadius: 12, border: "2px solid #E8E4DA",
              background: page === totalPages ? "#F5F3EE" : "#fff",
              cursor: page === totalPages ? "default" : "pointer",
              fontSize: 16, fontWeight: 600, color: page === totalPages ? "#ccc" : "#556B2F",
              fontFamily: FONT_BODY,
            }}>
              Next &rarr;
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes storyIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ==================== Global Search Overlay ====================
const GlobalSearch = ({ isOpen, onClose, allItems, onNavigate }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return allItems
      .filter((item) =>
        item.title.toLowerCase().includes(q) ||
        item.author.toLowerCase().includes(q) ||
        item.preview.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [query, allItems]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(44, 62, 26, 0.45)",
      backdropFilter: "blur(6px)",
      display: "flex", justifyContent: "center", paddingTop: 80,
      animation: "overlayIn 0.2s ease",
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 640, maxHeight: "calc(100vh - 140px)",
          background: "#fff", borderRadius: 22, overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
          animation: "modalIn 0.25s ease",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Search Input */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "20px 24px", borderBottom: "1px solid #E8E4DA",
        }}>
          <SearchIcon size={24} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all stories, milestones, capsules..."
            style={{
              flex: 1, border: "none", outline: "none", fontSize: 20,
              fontFamily: FONT_BODY, color: "#333",
              background: "transparent",
            }}
          />
          <button onClick={onClose} style={{
            background: "#F0EDE4", border: "none", borderRadius: 10,
            width: 36, height: 36, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#888",
          }}>
            <CloseIcon />
          </button>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 12px 16px" }}>
          {query.length < 2 && (
            <div style={{ padding: "40px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>{"\u{1F50D}"}</div>
              <p style={{ fontSize: 17, color: "#aaa", fontFamily: FONT_BODY }}>
                Start typing to search across everything
              </p>
              <p style={{ fontSize: 14, color: "#ccc", marginTop: 6, fontFamily: FONT_BODY }}>
                Search by title, family member name, or keywords
              </p>
            </div>
          )}

          {query.length >= 2 && results.length === 0 && (
            <div style={{ padding: "40px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>{"\u{1F614}"}</div>
              <p style={{ fontSize: 17, color: "#999", fontFamily: FONT_BODY }}>
                No results found for &ldquo;{query}&rdquo;
              </p>
              <p style={{ fontSize: 14, color: "#ccc", marginTop: 6, fontFamily: FONT_BODY }}>
                Try different keywords or check spelling
              </p>
            </div>
          )}

          {results.length > 0 && (
            <>
              <div style={{
                padding: "10px 14px 6px", fontSize: 13, fontWeight: 700,
                color: "#999", textTransform: "uppercase", letterSpacing: "0.5px",
                fontFamily: FONT_BODY,
              }}>
                {results.length} result{results.length !== 1 ? "s" : ""}
              </div>
              {results.map((item, i) => {
                const cfg = TYPE_CONFIG[item.type];
                return (
                  <button
                    key={item.id}
                    onClick={() => { onNavigate(item.type); onClose(); }}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 14,
                      padding: "14px 14px", borderRadius: 14, border: "none",
                      background: "transparent", cursor: "pointer", textAlign: "left",
                      transition: "background 0.15s",
                      animation: `resultIn 0.25s ease ${i * 0.03}s both`,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#F8F7F2"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{
                      width: 46, height: 46, borderRadius: 13, flexShrink: 0,
                      background: cfg.bg, border: `1px solid ${cfg.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22,
                    }}>
                      {cfg.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 16, fontWeight: 700, color: "#2C3E1A",
                        fontFamily: FONT_BODY,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 13, color: "#999", marginTop: 2, fontFamily: FONT_BODY }}>
                        {cfg.label} &middot; {item.author} &middot; {item.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                    <div style={{ color: "#D4CFC4", fontSize: 18, flexShrink: 0 }}>&rsaquo;</div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn { from { opacity: 0; transform: translateY(-16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes resultIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </div>
  );
};

// ==================== Dedicated Section Page ====================
const MILESTONE_FILTERS = [
  { key: "All", match: () => true },
  { key: "Coming up", match: (item) => item.status === "Coming up" },
  { key: "Lived \u2713", match: (item) => item.status === "Lived \u2713" },
  { key: "With Capsule", match: (item) => item.capsuleLinked },
];
const LOG_FILTERS = [
  { key: "All", match: () => true },
  { key: "Illness", match: (item) => item.category === "illness" },
  { key: "Condition", match: (item) => item.category === "condition" },
  { key: "Vehicle", match: (item) => item.category === "vehicle" },
];

const SectionPage = ({ type, items, familyMembers, onBack, navigate, onDelete }) => {
  const cfg = TYPE_CONFIG[type];
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Newest First");
  const [hoveredId, setHoveredId] = useState(null);
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("All");
  const [deletingId, setDeletingId] = useState(null);
  const perPage = 10;

  const filterConfig = type === 'milestone' ? MILESTONE_FILTERS
    : type === 'log' ? LOG_FILTERS
    : null;

  const newButtonLabel = type === 'milestone' ? 'Add a Moment' : type === 'log' ? 'New Log' : null;
  const newButtonRoute = type === 'milestone' ? '/my-life/add-milestone' : type === 'log' ? '/quick-log' : null;

  const filtered = items.filter((item) => {
    // Apply filter chip
    if (filterConfig && activeFilter !== "All") {
      const filterDef = filterConfig.find(f => f.key === activeFilter);
      if (filterDef && !filterDef.match(item)) return false;
    }
    if (search && !item.title.toLowerCase().includes(search.toLowerCase()) &&
      !(item.preview || '').toLowerCase().includes(search.toLowerCase()) &&
      !(item.author || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "Oldest First") return a.date - b.date;
    if (sortBy === "Most Loved") return b.loves - a.loves;
    return b.date - a.date;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const visible = sorted.slice((page - 1) * perPage, page * perPage);

  const handleItemClick = (item) => {
    if (item.type === 'story') navigate(`/stories/${item.id}`);
    else if (item.type === 'milestone') navigate(`/my-life/milestones/${item.id}`);
    else if (item.type === 'capsule') navigate('/timecapsule');
    else if (item.type === 'log') navigate(`/logs/${item.id}`);
  };

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    if (item.type === 'milestone') navigate(`/my-life/milestones/${item.id}/edit`);
    else if (item.type === 'log') navigate(`/logs/${item.id}/edit`);
    else if (item.type === 'story') navigate(`/stories/${item.id}/edit`);
  };

  const handleDeleteClick = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this ${cfg.label.toLowerCase()}? This cannot be undone.`)) return;
    setDeletingId(item.id);
    try {
      if (onDelete) await onDelete(item.id);
    } finally {
      setDeletingId(null);
    }
  };

  // Pick accent gradient based on type
  const accentGradient = `linear-gradient(180deg, ${cfg.border} 0%, ${cfg.accent || cfg.border} 100%)`;

  return (
    <div style={{ background: "#FAF9F5" }}>
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 28px 60px" }}>

        {/* Back button */}
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "none", border: "1.5px solid #D4CFC4", borderRadius: 10,
          padding: "9px 18px", cursor: "pointer", fontSize: 15, fontWeight: 600,
          color: "#556B2F", fontFamily: FONT_BODY,
          marginBottom: 22, transition: "all 0.15s",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#556B2F"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.borderColor = "#D4CFC4"; }}
        >
          <BackIcon /> Back to My Hub
        </button>

        {/* Title + New Button */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 24,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: cfg.bg, border: `1.5px solid ${cfg.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28,
            }}>{cfg.icon}</div>
            <div>
              <h1 style={{
                fontSize: 30, fontWeight: 800, color: "#2C3E1A", margin: 0,
                fontFamily: FONT_HEADING,
              }}>
                {cfg.plural}
              </h1>
              <p style={{ fontSize: 15, color: "#999", margin: "2px 0 0", fontFamily: FONT_BODY }}>
                {items.length} {cfg.plural.toLowerCase()}
              </p>
            </div>
          </div>

          {newButtonLabel && newButtonRoute && (
            <button onClick={() => navigate(newButtonRoute)} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "12px 24px", borderRadius: 14, border: "none",
              background: cfg.accent || "#556B2F", color: "#fff", cursor: "pointer",
              fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
              transition: "opacity 0.15s",
              boxShadow: `0 2px 8px ${cfg.accent || "#556B2F"}40`,
            }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              <PlusIcon /> {newButtonLabel}
            </button>
          )}
        </div>

        {/* Search & Sort */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, background: "#fff",
            border: "2px solid #DDD8CE", borderRadius: 14, padding: "12px 18px",
            flex: 1, minWidth: 260, transition: "border-color 0.2s",
          }}>
            <SearchIcon size={19} />
            <input
              type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={`Search ${cfg.plural.toLowerCase()}...`}
              style={{
                flex: 1, border: "none", outline: "none", fontSize: 17,
                fontFamily: FONT_BODY, background: "transparent", color: "#333",
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{
                background: "#E8E4DA", border: "none", borderRadius: "50%",
                width: 26, height: 26, cursor: "pointer", fontSize: 13, color: "#888",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{"\u2715"}</button>
            )}
          </div>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} style={{
            padding: "12px 16px", borderRadius: 12, border: "2px solid #DDD8CE",
            background: "#fff", fontSize: 16, fontFamily: FONT_BODY,
            color: "#556B2F", fontWeight: 600, cursor: "pointer",
          }}>
            <option>Newest First</option>
            <option>Oldest First</option>
            <option>Most Loved</option>
          </select>
        </div>

        {/* Filter chips */}
        {filterConfig && (
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            {filterConfig.map(({ key, match }) => {
              const count = key === "All" ? items.length : items.filter(match).length;
              return (
                <button
                  key={key}
                  onClick={() => { setActiveFilter(key); setPage(1); }}
                  style={{
                    padding: "8px 18px", borderRadius: 24, border: "none", cursor: "pointer",
                    fontSize: 15, fontWeight: activeFilter === key ? 700 : 500,
                    fontFamily: FONT_BODY,
                    background: activeFilter === key ? (cfg.accent || "#556B2F") : "#EDEADF",
                    color: activeFilter === key ? "#fff" : "#666",
                    transition: "all 0.15s",
                  }}
                >
                  {key} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Results count */}
        <p style={{ fontSize: 14, color: "#bbb", marginBottom: 14, fontFamily: FONT_BODY }}>
          Showing {visible.length} of {sorted.length} {cfg.plural.toLowerCase()}
        </p>

        {/* Item List — same row-card style as My Stories */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((item, i) => {
            const isHovered = hoveredId === item.id;
            const isDeleting = deletingId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: "flex", alignItems: "stretch", gap: 0,
                  background: "#fff", borderRadius: 16,
                  border: `1.5px solid ${isHovered ? cfg.border : "#E8E4DA"}`,
                  overflow: "hidden", cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isHovered ? "0 6px 20px rgba(85,107,47,0.08)" : "0 1px 4px rgba(0,0,0,0.02)",
                  transform: isHovered ? "translateY(-1px)" : "translateY(0)",
                  animation: `storyIn 0.35s ease ${i * 0.05}s both`,
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                {/* Left accent bar */}
                <div style={{
                  width: 5, flexShrink: 0,
                  background: isHovered ? accentGradient
                    : `linear-gradient(180deg, ${cfg.border} 0%, ${cfg.bg} 100%)`,
                  transition: "background 0.25s",
                }} />

                {/* Icon thumbnail */}
                <div style={{
                  width: 72, height: 72, flexShrink: 0, margin: "14px 0 14px 16px",
                  borderRadius: 14, overflow: "hidden",
                  background: cfg.bg, border: `1.5px solid ${cfg.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 30,
                  transition: "transform 0.2s",
                  transform: isHovered ? "scale(1.05)" : "scale(1)",
                }}>
                  {cfg.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, padding: "14px 20px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                    <h3 style={{
                      fontSize: 18, fontWeight: 700, color: "#2C3E1A", margin: 0,
                      fontFamily: FONT_HEADING,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      flex: 1,
                    }}>
                      {item.title}
                    </h3>
                    {item.status && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
                        background: item.status === "scheduled" ? "#E3F2FD"
                          : item.status === "delivered" ? "#E8F5E9"
                          : item.status === "Upcoming" ? "#FFF8E1"
                          : item.status === "Completed" ? "#E8F5E9"
                          : item.status === "Capsule Linked" ? "#E8EAF6"
                          : "#F0EDE4",
                        color: item.status === "scheduled" ? "#1565C0"
                          : item.status === "delivered" ? "#2E7D32"
                          : item.status === "Upcoming" ? "#F57F17"
                          : item.status === "Completed" ? "#2E7D32"
                          : item.status === "Capsule Linked" ? "#283593"
                          : "#666",
                        fontFamily: FONT_BODY,
                      }}>
                        {item.status}
                      </span>
                    )}
                  </div>
                  {item.preview && (
                    <p style={{
                      fontSize: 15, color: "#999", margin: "0 0 10px",
                      fontFamily: FONT_BODY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {item.preview}
                    </p>
                  )}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 10,
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 14,
                      fontSize: 14, fontFamily: FONT_BODY,
                    }}>
                      <span style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 4 }}>
                        {"\u{1F4C5}"} {item.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {item.author && (
                        <span style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 4 }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: "50%", background: "#556B2F",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", fontSize: 11, fontWeight: 700,
                          }}>{item.author.charAt(0)}</div>
                          {item.author}
                        </span>
                      )}
                      {item.location && (
                        <span style={{ color: "#bbb", display: "flex", alignItems: "center", gap: 4 }}>
                          {"\u{1F4CD}"} {item.location}
                        </span>
                      )}
                      {item.loves > 0 && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#E65100" }}>
                          <HeartIcon filled /> {item.loves}
                        </span>
                      )}
                      {item.deliveryDate && (
                        <span style={{ color: "#1565C0", display: "flex", alignItems: "center", gap: 4 }}>
                          {"\u{1F5D3}\uFE0F"} {item.deliveryDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Edit/Delete hover buttons */}
                {(type === 'milestone' || type === 'log') && (
                  <div style={{
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    gap: 6, padding: "0 16px", flexShrink: 0,
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.2s",
                  }}>
                    <button
                      onClick={(e) => handleEditClick(e, item)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "7px 14px", borderRadius: 9, border: "1.5px solid #D4CFC4",
                        background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
                        color: cfg.accent || "#556B2F", fontFamily: FONT_BODY,
                        transition: "all 0.15s", whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = cfg.accent || "#556B2F"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = cfg.accent || "#556B2F"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = cfg.accent || "#556B2F"; e.currentTarget.style.borderColor = "#D4CFC4"; }}
                    >
                      <EditIcon /> Edit
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(e, item)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "7px 14px", borderRadius: 9, border: "1.5px solid #E8E4DA",
                        background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
                        color: "#999", fontFamily: FONT_BODY,
                        transition: "all 0.15s", whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#FFF5F5"; e.currentTarget.style.color = "#D32F2F"; e.currentTarget.style.borderColor = "#FFCDD2"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#999"; e.currentTarget.style.borderColor = "#E8E4DA"; }}
                    >
                      <TrashIcon /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {visible.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: 52, marginBottom: 16, opacity: 0.5 }}>{"\u{1F50D}"}</div>
            <p style={{ fontSize: 18, color: "#999", fontFamily: FONT_BODY }}>
              No {cfg.plural.toLowerCase()} match your search.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, marginTop: 32,
          }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{
              padding: "11px 22px", borderRadius: 12, border: "2px solid #E8E4DA",
              background: page === 1 ? "#F5F3EE" : "#fff",
              cursor: page === 1 ? "default" : "pointer",
              fontSize: 16, fontWeight: 600, color: page === 1 ? "#ccc" : "#556B2F",
              fontFamily: FONT_BODY,
            }}>
              &larr; Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={{
                width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
                fontSize: 16, fontWeight: page === p ? 800 : 500,
                background: page === p ? "#556B2F" : "transparent",
                color: page === p ? "#fff" : "#777",
                fontFamily: FONT_BODY,
              }}>
                {p}
              </button>
            ))}
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{
              padding: "11px 22px", borderRadius: 12, border: "2px solid #E8E4DA",
              background: page === totalPages ? "#F5F3EE" : "#fff",
              cursor: page === totalPages ? "default" : "pointer",
              fontSize: 16, fontWeight: 600, color: page === totalPages ? "#ccc" : "#556B2F",
              fontFamily: FONT_BODY,
            }}>
              Next &rarr;
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes storyIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ==================== Summary Section ====================
const SummarySection = ({ type, title, items, totalCount, onViewAll, navigate, style = {}, childTags = null }) => {
  const cfg = TYPE_CONFIG[type];

  const handleItemClick = (item) => {
    if (item.type === 'story') navigate(`/stories/${item.id}`);
    else if (item.type === 'milestone') navigate(`/my-life/milestones/${item.id}`);
    else if (item.type === 'capsule') navigate('/timecapsule');
    else if (item.type === 'log') navigate(`/logs/${item.id}`);
  };

  // Helper to get child tags for a story item
  const getItemChildTags = (item) => {
    if (!childTags || !item.event_people || item.event_people.length === 0) return [];
    return childTags.children
      .filter(child => item.event_people.includes(child.id))
      .map((child) => {
        const colorIdx = childTags.children.indexOf(child);
        const color = childTags.colors[colorIdx % childTags.colors.length];
        return { name: child.first_name, color };
      });
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 20, border: "1px solid #E8E4DA",
      padding: 26, boxShadow: "0 1px 6px rgba(0,0,0,0.03)", ...style,
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 18,
      }}>
        <h2 style={{
          fontSize: 21, fontWeight: 800, color: "#2C3E1A", margin: 0,
          fontFamily: FONT_HEADING,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          {cfg.icon} {title}
        </h2>
        <button onClick={onViewAll} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 15, fontWeight: 700, color: "#556B2F",
          fontFamily: FONT_BODY,
          display: "flex", alignItems: "center", gap: 5,
          padding: "6px 10px", borderRadius: 8,
          transition: "background 0.15s",
        }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#F0EDE4"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          View All {totalCount} <ArrowRight />
        </button>
      </div>

      {items.length > 0 ? items.map((item, i) => {
        const itemChildTags = getItemChildTags(item);
        return (
          <div key={item.id} style={{
            display: "flex", alignItems: "center", gap: 16,
            padding: "15px 18px", borderRadius: 16, background: "#F8F7F2",
            marginBottom: i < items.length - 1 ? 10 : 0,
            border: "1px solid #EDEADF", cursor: "pointer",
            transition: "background 0.15s, transform 0.15s",
            animation: `fadeIn 0.35s ease ${i * 0.08}s both`,
          }}
            onClick={() => handleItemClick(item)}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F0EDE4"; e.currentTarget.style.transform = "translateX(4px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#F8F7F2"; e.currentTarget.style.transform = "translateX(0)"; }}
          >
            <div style={{
              width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
              background: itemChildTags.length > 0 ? itemChildTags[0].color.avatar : "#556B2F",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 18, fontWeight: 700,
            }}>
              {itemChildTags.length > 0 ? itemChildTags[0].name.charAt(0) : item.author.charAt(0)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h4 style={{
                  fontSize: 17, fontWeight: 700, color: "#2C3E1A", margin: 0,
                  fontFamily: FONT_BODY,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  flex: 1, minWidth: 0,
                }}>
                  {item.title}
                </h4>
                {/* Child tags */}
                {itemChildTags.map((tag, tagIdx) => (
                  <span key={tagIdx} style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                    background: tag.color.bg, color: tag.color.text,
                    fontFamily: FONT_BODY,
                  }}>
                    {tag.name}
                  </span>
                ))}
                {item.hasPendingVoices && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 10px', borderRadius: 12,
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                    background: '#FFF7F0', color: '#E65100',
                    border: '1px solid #F0C070',
                    fontFamily: FONT_BODY,
                  }}>
                    🎤 Identify voice
                  </span>
                )}
              </div>
              <div style={{ fontSize: 14, color: "#999", marginTop: 4, fontFamily: FONT_BODY }}>
                {item.author} &middot; {item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {item.loves > 0 && <span style={{ marginLeft: 10, color: "#E65100" }}>{"\u{1F49A}"} {item.loves}</span>}
              </div>
            </div>
            <div style={{ color: "#D4CFC4", fontSize: 22, flexShrink: 0 }}>&rsaquo;</div>
          </div>
        );
      }) : (
        <div style={{ textAlign: "center", padding: "30px 20px", color: "#999" }}>
          <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.5 }}>{cfg.icon}</div>
          <p style={{ fontSize: 15, fontFamily: FONT_BODY }}>No {cfg.plural.toLowerCase()} yet</p>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ==================== Main Hub Component ====================
function Hub() {
  const navigate = useNavigate();
  const { activeFamilyId } = useFamily();
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showMemoryOptions, setShowMemoryOptions] = useState(false);
  // Daily question
  const {
    questionData,
  } = useDailyQuestion();

  // Unified item storage
  const [items, setItems] = useState({ story: [], milestone: [], capsule: [], log: [] });
  const [stats, setStats] = useState({ story: 0, milestone: 0, capsule: 0, log: 0 });

  // Children data for person filter tabs
  const [children, setChildren] = useState([]);
  const [personFilter, setPersonFilter] = useState('all');

  // Child accent color constants
  const CHILD_COLORS_HUB = [
    { key: 'orange', bg: '#fff3ed', border: '#E65100', text: '#E65100', avatar: '#E65100' },
    { key: 'blue', bg: '#e6f1fb', border: '#185FA5', text: '#185FA5', avatar: '#185FA5' },
  ];

  // Fetch dashboard data and transform to unified format
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const userData = getUser() || {};
      const userName = userData.first_name || 'You';
      const userPersonId = userData.person_id || userData.id || null;

      // Fetch all data in parallel (including children data)
      const [dashboardResult, milestonesResult, capsulesResult, logsResult, childrenResult, pendingVoicesResult] = await Promise.allSettled([
        axiosInstance.get('/dashboard/'),
        axiosInstance.get('/chapter-milestones/'),
        axiosInstance.get('/timecapsules/'),
        axiosInstance.get('/logs/'),
        axiosInstance.get('/genealogy/people/my_family_grouped/'),
        getPendingConfirmations(),
      ]);

      // Extract children from grouped family response
      if (childrenResult.status === 'fulfilled') {
        const groupedData = childrenResult.value.data;
        const minorChildren = (groupedData.children || []).filter(c => c.is_minor);
        setChildren(minorChildren);
      }

      // Get story IDs with pending voice identification
      const pendingVoicesData = pendingVoicesResult.status === 'fulfilled' ? pendingVoicesResult.value : {};
      const pendingStoryIds = new Set((pendingVoicesData.stories || []).map(s => String(s.id)));
      const pendingJobId = (pendingVoicesData.job_ids || [])[0] || null;

      // Transform stories from dashboard response
      const dashboardData = dashboardResult.status === 'fulfilled' ? dashboardResult.value.data : {};
      const storyItems = (dashboardData.stories || [])
        .filter(story => !story.sharer || story.sharer.relationship === 'You')
        .map((story, index) => ({
          id: story.id,
          type: 'story',
          title: story.title || 'Untitled Story',
          subtitle: story.preview || story.description || '',
          date: new Date(story.sharedAt || story.created_at || Date.now()),
          author: userName,
          authorPersonId: story.sharer?.id || userPersonId,
          location: story.location || null,
          loves: story.engagement?.reactionCount || 0,
          comments: story.engagement?.commentCount || 0,
          lovedBy: story.engagement?.reactionCount > 0
            ? (story.engagement?.readBy || []).slice(0, 3).map(r => r.firstName)
            : [],
          commentedBy: story.lastComment
            ? [story.lastComment.authorName].filter(Boolean)
            : [],
          hasMedia: !!(story.hasAudio || story.hasVideo || story.thumbnailUrl),
          mediaType: story.hasVideo ? 'video' : story.hasAudio ? 'audio' : story.thumbnailUrl ? 'image' : null,
          preview: story.preview || story.description || '',
          status: story.status || story.privacy_level || 'shared',
          deliveryDate: null,
          thumbnailUrl: story.thumbnailUrl || null,
          thumbGradient: THUMB_GRADIENTS[index % THUMB_GRADIENTS.length],
          thumbEmoji: THUMB_EMOJIS[index % THUMB_EMOJIS.length],
          event_people: story.event_people || [],
          hasPendingVoices: pendingStoryIds.has(String(story.id)),
          pendingJobId: pendingStoryIds.has(String(story.id)) ? pendingJobId : null,
        }));
      storyItems.sort((a, b) => b.date - a.date);

      // Transform milestones
      let milestoneItems = [];
      if (milestonesResult.status === 'fulfilled') {
        const milestonesData = milestonesResult.value.data.results || milestonesResult.value.data || [];
        milestoneItems = milestonesData.map(m => {
          // Build event date from year/month fields (the "when did it happen" date)
          let eventDate;
          if (m.year) {
            eventDate = new Date(m.year, m.month ? m.month - 1 : 0, 1);
          } else if (m.date) {
            eventDate = new Date(m.date);
          } else {
            eventDate = new Date(m.created_at || Date.now());
          }
          return {
            id: m.id,
            type: 'milestone',
            title: m.title || m.name || 'Untitled Milestone',
            date: eventDate,
            year: m.year || null,
            month: m.month || null,
            author: m.user_name || userName,
            personName: m.user_name || null,
            personId: m.user_id || null,
            isMinor: m.is_minor || false,
            location: m.location || null,
            loves: 0,
            hasMedia: false,
            mediaType: null,
            preview: m.description || m.text || '',
            status: getMilestoneStatusLabel(m),
            deliveryDate: null,
            category: m.quick_pick_type || m.category || 'other',
            capsuleLinked: !!(m.has_capsule || m.linked_capsule),
          };
        });
        milestoneItems.sort((a, b) => b.date - a.date);
      } else {
        console.error('Error fetching milestones:', milestonesResult.reason);
      }

      // Transform time capsules
      let capsuleItems = [];
      if (capsulesResult.status === 'fulfilled') {
        const capsulesData = capsulesResult.value.data.results || capsulesResult.value.data || [];
        capsuleItems = capsulesData.map(c => ({
          id: c.id,
          type: 'capsule',
          title: c.title || c.name || 'Untitled Capsule',
          date: c.created_at ? new Date(c.created_at) : new Date(),
          author: userName,
          location: null,
          loves: 0,
          hasMedia: false,
          mediaType: null,
          preview: `To: ${formatRecipients(c.recipients || c.recipient)}`,
          status: getCapsuleStatus(c).toLowerCase(),
          deliveryDate: c.delivery_date ? new Date(c.delivery_date) : null,
        }));
        capsuleItems.sort((a, b) => b.date - a.date);
      } else {
        console.error('Error fetching time capsules:', capsulesResult.reason);
      }

      // Transform logs
      let logItems = [];
      if (logsResult.status === 'fulfilled') {
        const logsData = logsResult.value.data.results || logsResult.value.data || [];
        const categoryLabels = { illness: 'Illness', condition: 'Condition', vehicle: 'Vehicle' };
        logItems = logsData.map(l => ({
          id: l.id,
          type: 'log',
          title: `${categoryLabels[l.category] || l.category}${l.sub_type ? ' \u2014 ' + l.sub_type.replace(/_/g, ' ') : ''}`,
          date: l.created_at ? new Date(l.created_at) : new Date(),
          author: l.subject_name_display || l.subject_name || userName,
          location: l.location || null,
          loves: 0,
          hasMedia: false,
          mediaType: null,
          preview: l.note || '',
          status: null,
          deliveryDate: null,
          category: l.category || null,
        }));
        logItems.sort((a, b) => b.date - a.date);
      } else {
        console.error('Error fetching logs:', logsResult.reason);
      }

      setItems({ story: storyItems, milestone: milestoneItems, capsule: capsuleItems, log: logItems });
      setStats({
        story: dashboardData.stats?.storyCount || storyItems.length,
        milestone: milestoneItems.length,
        capsule: capsuleItems.length,
        log: logItems.length,
      });
    } catch (error) {
      console.error('Error fetching hub data:', error);
      setItems({ story: [], milestone: [], capsule: [], log: [] });
      setStats({ story: 0, milestone: 0, capsule: 0, log: 0 });
    } finally {
      setLoading(false);
    }
  }, [activeFamilyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build combined items for search
  const allItems = useMemo(() => {
    return [...items.story, ...items.milestone, ...items.capsule, ...items.log]
      .sort((a, b) => b.date - a.date);
  }, [items]);

  // Derive unique family members from items
  const familyMembers = useMemo(() => {
    const members = new Set();
    allItems.forEach(item => members.add(item.author));
    return Array.from(members);
  }, [allItems]);

  const totalItems = allItems.length;

  // Keyboard shortcut for search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Filter stories by selected person
  const filteredStories = useMemo(() => {
    if (personFilter === 'all') return items.story;
    return items.story.filter(story => {
      // Check if story's event_people includes the selected person
      if (story.event_people && story.event_people.includes(personFilter)) return true;
      // Also match if the story author is the selected person (for self filter)
      if (story.authorPersonId === personFilter) return true;
      return false;
    });
  }, [items.story, personFilter]);

  // Helper to get child info for a story's event_people
  const getChildTagsForStory = (story) => {
    if (!story.event_people || children.length === 0) return [];
    return children
      .filter(child => story.event_people.includes(child.id))
      .map((child, idx) => {
        const colorIdx = children.indexOf(child);
        const color = CHILD_COLORS_HUB[colorIdx % CHILD_COLORS_HUB.length];
        return { name: child.first_name, color };
      });
  };

  // Slices for dashboard summaries
  const recentStories = filteredStories.slice(0, 3);
  const recentMilestones = items.milestone.slice(0, 2);
  const recentLogs = items.log.slice(0, 3);
  const upcomingCapsules = items.capsule.filter(c => c.status === 'scheduled').slice(0, 2);

  // Daily question for the prompt card
  const dailyQuestion = questionData?.has_question ? questionData.question : null;
  const dailyQuestionAnswered = questionData?.user_status === 'answered';
  const dailyQuestionSkipped = questionData?.user_status === 'skipped';
  const dailyQuestionAnswer = questionData?.answer || null;

  if (loading) {
    return (
      <HubLayout activeView="hub">
        <div className="hub-container">
          <div className="hub-loading">
            <div className="hub-loading-spinner"></div>
            <p>Loading your hub...</p>
          </div>
        </div>
      </HubLayout>
    );
  }

  return (
    <HubLayout activeView="hub">
      {/* Global Search Overlay */}
      <GlobalSearch
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        allItems={allItems}
        onNavigate={(type) => setCurrentSection(type)}
      />

      {/* Content */}
      {currentSection === 'story' ? (
        <MyStoriesSection
          stories={items.story}
          onBack={() => setCurrentSection(null)}
          navigate={navigate}
        />
      ) : currentSection === 'milestone' ? (
        <MilestonesTimelinePage
          items={items.milestone}
          onBack={() => setCurrentSection(null)}
          navigate={navigate}
          onDelete={async (id) => {
            await axiosInstance.delete(`/chapter-milestones/${id}/`);
            setItems(prev => ({ ...prev, milestone: prev.milestone.filter(item => item.id !== id) }));
            setStats(prev => ({ ...prev, milestone: Math.max(0, prev.milestone - 1) }));
          }}
        />
      ) : currentSection ? (
        <SectionPage
          type={currentSection}
          items={items[currentSection] || []}
          familyMembers={familyMembers}
          onBack={() => setCurrentSection(null)}
          navigate={navigate}
          onDelete={async (id) => {
            const endpoint = currentSection === 'milestone' ? `/chapter-milestones/${id}/` : `/logs/${id}/`;
            await axiosInstance.delete(endpoint);
            // Remove from local state
            setItems(prev => ({
              ...prev,
              [currentSection]: prev[currentSection].filter(item => item.id !== id),
            }));
            setStats(prev => ({
              ...prev,
              [currentSection]: Math.max(0, prev[currentSection] - 1),
            }));
          }}
        />
      ) : (
        <div style={{ background: "#FAF9F5" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto", padding: "28px 28px 60px" }}>

            {/* Family context banner for multi-family users */}
            <FamilyContextBanner />

            {/* Hub Header + Search Bar */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18 }}>
                <div>
                  <h1 style={{
                    fontSize: 32, fontWeight: 800, color: "#2C3E1A", margin: "0 0 4px",
                    fontFamily: FONT_HEADING,
                  }}>
                    My Hub
                  </h1>
                  <p style={{ fontSize: 16, color: "#999", margin: 0, fontFamily: FONT_BODY }}>
                    Your family legacy at a glance{totalItems > 0 ? ` \u2014 ${totalItems} memories and counting` : ''}
                  </p>
                </div>
              </div>

              {/* Add a Memory Bar */}
              <div style={{ marginBottom: 14 }}>
                <button
                  onClick={() => setShowMemoryOptions(!showMemoryOptions)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 16,
                    background: 'linear-gradient(135deg, #eef2e4 0%, #e8efc8 100%)',
                    border: '3px solid #556B2F', borderRadius: 16,
                    padding: '14px 20px', cursor: 'pointer', textAlign: 'left',
                    transition: 'box-shadow 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(85,107,47,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{
                    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                    background: '#556B2F',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 26, fontWeight: 800,
                  }}>+</div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 19, fontWeight: 800, color: '#2C3E1A',
                      fontFamily: FONT_HEADING, marginBottom: 2,
                    }}>
                      Add a Memory
                    </div>
                    <div style={{
                      fontSize: 14, color: '#666', fontFamily: FONT_BODY,
                    }}>
                      A story, a quick fact, or a message for the future
                    </div>
                  </div>
                  <div style={{
                    fontSize: 22, color: '#556B2F', fontWeight: 700, flexShrink: 0,
                    transition: 'transform 0.2s',
                    transform: showMemoryOptions ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}>
                    {"\u2192"}
                  </div>
                </button>

                {/* Expandable Options */}
                <div style={{
                  maxHeight: showMemoryOptions ? 320 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.35s ease, opacity 0.25s ease',
                  opacity: showMemoryOptions ? 1 : 0,
                }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16,
                    paddingTop: 16,
                  }}>
                    {/* Tell the Story Card */}
                    <button
                      onClick={() => navigate('/tell-a-story')}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        padding: 20, borderRadius: 16,
                        border: '2px solid #A5D6A7', background: '#fff',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#556B2F'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(85,107,47,0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#A5D6A7'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: '#E8F5E9', border: '1.5px solid #A5D6A7',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, marginBottom: 12,
                      }}>{"\u{1F4D6}"}</div>
                      <div style={{
                        fontSize: 18, fontWeight: 800, color: '#2C3E1A',
                        fontFamily: FONT_HEADING, marginBottom: 4,
                      }}>Tell the Story</div>
                      <div style={{
                        fontSize: 14, color: '#777', fontFamily: FONT_BODY,
                        lineHeight: 1.4, marginBottom: 10,
                      }}>Share the full narrative with photos, audio, and emotion</div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: '#556B2F',
                        background: '#E8F5E9', padding: '4px 10px', borderRadius: 8,
                        fontFamily: FONT_BODY,
                      }}>5-15 minutes</div>
                    </button>

                    {/* Just the Facts Card */}
                    <button
                      onClick={() => navigate('/quick-log')}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        padding: 20, borderRadius: 16,
                        border: '2px solid #b0c4d8', background: '#fff',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#4a6178'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(74,97,120,0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#b0c4d8'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: '#eaf0f6', border: '1.5px solid #b0c4d8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, marginBottom: 12,
                      }}>{"\u{1F4CB}"}</div>
                      <div style={{
                        fontSize: 18, fontWeight: 800, color: '#2C3E1A',
                        fontFamily: FONT_HEADING, marginBottom: 4,
                      }}>Just the Facts</div>
                      <div style={{
                        fontSize: 14, color: '#777', fontFamily: FONT_BODY,
                        lineHeight: 1.4, marginBottom: 10,
                      }}>Record the who, what, and when &mdash; quick and structured</div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: '#4a6178',
                        background: '#eaf0f6', padding: '4px 10px', borderRadius: 8,
                        fontFamily: FONT_BODY,
                      }}>Under 60 seconds</div>
                    </button>

                    {/* Achieved a Milestone Card */}
                    <button
                      onClick={() => navigate('/my-life/add-milestone')}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        padding: 20, borderRadius: 16,
                        border: '2px solid #E8D49B', background: '#fff',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#B8860B'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(184,134,11,0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8D49B'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: '#FFF7E0', border: '1.5px solid #E8D49B',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, marginBottom: 12,
                      }}>{"\u{1F3C6}"}</div>
                      <div style={{
                        fontSize: 18, fontWeight: 800, color: '#2C3E1A',
                        fontFamily: FONT_HEADING, marginBottom: 4,
                      }}>Add a Big Moment</div>
                      <div style={{
                        fontSize: 14, color: '#777', fontFamily: FONT_BODY,
                        lineHeight: 1.4, marginBottom: 10,
                      }}>Mark a key life moment &mdash; graduation, wedding, first steps</div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: '#B8860B',
                        background: '#FFF7E0', padding: '4px 10px', borderRadius: 8,
                        fontFamily: FONT_BODY,
                      }}>Under 2 minutes</div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Global Search Bar */}
              <button
                onClick={() => setSearchOpen(true)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 14,
                  background: "#fff", border: "2px solid #D4CFC4", borderRadius: 16,
                  padding: "16px 22px", cursor: "pointer", textAlign: "left",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#556B2F"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(85,107,47,0.1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#D4CFC4"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <SearchIcon size={22} />
                <span style={{ fontSize: 18, color: "#aaa", fontFamily: FONT_BODY }}>
                  Search all stories, milestones, capsules...
                </span>
                <span style={{
                  fontSize: 12, background: "#F0EDE4", borderRadius: 6,
                  padding: "3px 8px", marginLeft: "auto", color: "#999", fontFamily: FONT_BODY,
                }}>
                  {"\u2318"}K
                </span>
              </button>
            </div>

            {/* Summary Stats */}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14,
              marginBottom: 32,
            }}>
              {Object.entries(TYPE_CONFIG).map(([type, cfg]) => (
                <button
                  key={type}
                  onClick={() => type === 'capsule' ? navigate('/timecapsule') : setCurrentSection(type)}
                  style={{
                    background: "#fff", borderRadius: 14, border: `2px solid ${cfg.border}`,
                    padding: "14px 18px", cursor: "pointer", textAlign: "left",
                    transition: "all 0.2s", position: "relative", overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{
                    position: "absolute", top: -14, right: -14, width: 50, height: 50,
                    background: cfg.bg, borderRadius: "50%", opacity: 0.4,
                  }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22 }}>{cfg.icon}</span>
                    <span style={{
                      fontSize: 28, fontWeight: 800, color: cfg.accent,
                      fontFamily: FONT_BODY, lineHeight: 1,
                    }}>
                      {stats[type]}
                    </span>
                    <span style={{
                      fontSize: 14, fontWeight: 600, color: "#888",
                      fontFamily: FONT_BODY,
                    }}>
                      {cfg.plural}
                    </span>
                  </div>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 4, marginTop: 6,
                    fontSize: 13, fontWeight: 700, color: cfg.accent,
                    fontFamily: FONT_BODY,
                  }}>
                    View All <ArrowRight />
                  </div>
                </button>
              ))}
            </div>

            {/* Person Filter Tabs — only show when user has minor children */}
            {children.length > 0 && (
              <div style={{
                display: 'flex', gap: 10, marginBottom: 24,
                overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                paddingBottom: 4,
              }}>
                {/* Everyone tab */}
                <button
                  onClick={() => setPersonFilter('all')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 18px', borderRadius: 24, border: 'none',
                    background: personFilter === 'all' ? '#556B2F' : '#EDEADF',
                    color: personFilter === 'all' ? '#fff' : '#666',
                    fontSize: 15, fontWeight: personFilter === 'all' ? 700 : 500,
                    fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  Everyone
                </button>

                {/* Self tab */}
                {(() => {
                  const userData = getUser() || {};
                  const selfId = userData.person_id || userData.id || null;
                  const selfName = userData.first_name || 'Me';
                  return (
                    <button
                      onClick={() => setPersonFilter(selfId || 'self')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 18px', borderRadius: 24, border: 'none',
                        background: personFilter === selfId || personFilter === 'self' ? '#556B2F' : '#EDEADF',
                        color: personFilter === selfId || personFilter === 'self' ? '#fff' : '#666',
                        fontSize: 15, fontWeight: (personFilter === selfId || personFilter === 'self') ? 700 : 500,
                        fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: personFilter === selfId ? '#fff' : '#556B2F',
                        color: personFilter === selfId ? '#556B2F' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {selfName.charAt(0).toUpperCase()}
                      </div>
                      {selfName}
                    </button>
                  );
                })()}

                {/* Child tabs */}
                {children.map((child, idx) => {
                  const color = CHILD_COLORS_HUB[idx % CHILD_COLORS_HUB.length];
                  const isActive = personFilter === child.id;
                  return (
                    <button
                      key={child.id}
                      onClick={() => setPersonFilter(child.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 18px', borderRadius: 24, border: 'none',
                        background: isActive ? color.border : '#EDEADF',
                        color: isActive ? '#fff' : '#666',
                        fontSize: 15, fontWeight: isActive ? 700 : 500,
                        fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: isActive ? '#fff' : color.avatar,
                        color: isActive ? color.avatar : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {child.first_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      {child.first_name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Main Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 350px", gap: 26 }}>

              {/* Left Column */}
              <div>
                <SpeakerNotificationBanner
                  onIdentifyClick={(jId) => navigate(`/identify-voices/${jId}`)}
                />
                <SummarySection
                  type="story"
                  title={personFilter === 'all' ? "My Recent Stories" : `Stories`}
                  items={recentStories}
                  totalCount={personFilter === 'all' ? stats.story : filteredStories.length}
                  onViewAll={() => setCurrentSection("story")}
                  navigate={navigate}
                  childTags={children.length > 0 ? { children, colors: CHILD_COLORS_HUB } : null}
                />
                <div style={{ marginTop: 26 }}>
                  <WisdomMomentsFeed />
                </div>
                <div style={{ marginTop: 26 }}>
                  <MilestoneSummaryTimeline
                    items={items.milestone}
                    totalCount={stats.milestone}
                    onViewAll={() => setCurrentSection("milestone")}
                    navigate={navigate}
                  />
                </div>
                <SummarySection
                  type="log"
                  title="Recent Logs"
                  items={recentLogs}
                  totalCount={stats.log}
                  onViewAll={() => setCurrentSection("log")}
                  navigate={navigate}
                  style={{ marginTop: 26 }}
                />
              </div>

              {/* Right Sidebar */}
              <div>
                {/* Today's Prompt */}
                <div style={{
                  background: "#556B2F", borderRadius: 20, padding: 26, marginBottom: 22,
                  position: "relative", overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute", top: -24, right: -24, width: 110, height: 110,
                    background: "rgba(255,255,255,0.07)", borderRadius: "50%",
                  }} />
                  <div style={{
                    position: "absolute", bottom: -16, left: -16, width: 70, height: 70,
                    background: "rgba(255,255,255,0.05)", borderRadius: "50%",
                  }} />
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: "#FFD54F",
                    textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14,
                    fontFamily: FONT_BODY,
                  }}>
                    {dailyQuestion
                      ? "Question of the Day"
                      : "Today's Prompt"
                    }
                  </div>
                  {dailyQuestion && (
                    <div style={{
                      display: "inline-block",
                      background: "rgba(255,255,255,0.15)",
                      color: "rgba(255,255,255,0.85)",
                      padding: "3px 10px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      marginBottom: 12,
                      fontFamily: FONT_BODY,
                    }}>
                      {dailyQuestion.chapter_title}
                    </div>
                  )}
                  <p style={{
                    fontSize: 20, fontWeight: 600, color: "#fff", lineHeight: 1.45,
                    margin: "0 0 20px", fontFamily: FONT_HEADING,
                    fontStyle: "italic",
                  }}>
                    &ldquo;{dailyQuestion
                      ? dailyQuestion.question_text
                      : "What is one tradition from your childhood that you wish your children could experience?"
                    }&rdquo;
                  </p>
                  {dailyQuestion && !dailyQuestionAnswered ? (
                    <div>
                      {dailyQuestionSkipped && (
                        <div style={{
                          fontSize: 13, color: "rgba(255,255,255,0.55)", fontFamily: FONT_BODY,
                          marginBottom: 10,
                        }}>
                          Skipped earlier — you can still answer!
                        </div>
                      )}
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({
                            storyType: 'family_memory',
                            chapter: dailyQuestion.chapter_id,
                            title: dailyQuestion.question_text,
                            sharing: 'all_families',
                            dailyQuestionId: dailyQuestion.id,
                          });
                          navigate(`/tell-a-story?${params.toString()}`);
                        }}
                        style={{
                          padding: "12px 26px", borderRadius: 14, border: "none",
                          background: "#E65100", color: "#fff", cursor: "pointer",
                          fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                          display: "flex", alignItems: "center", gap: 8,
                          transition: "opacity 0.2s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                      >
                        {"\u270D\uFE0F"} Answer Now
                      </button>
                    </div>
                  ) : dailyQuestionAnswered ? (
                    <div>
                      {/* Answer preview card */}
                      <div style={{
                        background: "rgba(255,255,255,0.12)",
                        borderRadius: 12,
                        padding: "12px 14px",
                        marginBottom: 12,
                      }}>
                        <div style={{
                          fontSize: 12, color: "rgba(255,255,255,0.5)", fontFamily: FONT_BODY,
                          fontWeight: 600, marginBottom: 6,
                          display: "flex", alignItems: "center", gap: 6,
                        }}>
                          {"\u2705"} Your answer
                        </div>
                        {dailyQuestionAnswer?.text ? (
                          <p style={{
                            fontSize: 14, color: "rgba(255,255,255,0.9)", fontFamily: FONT_BODY,
                            lineHeight: 1.5, margin: 0,
                            display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}>
                            {dailyQuestionAnswer.text}
                          </p>
                        ) : (
                          <p style={{
                            fontSize: 14, color: "rgba(255,255,255,0.6)", fontFamily: FONT_BODY,
                            fontStyle: "italic", margin: 0,
                          }}>
                            Story saved
                          </p>
                        )}
                      </div>
                      {/* Edit button */}
                      <button
                        onClick={() => {
                          if (dailyQuestionAnswer?.life_event_id) {
                            navigate(`/stories/${dailyQuestionAnswer.life_event_id}/edit`);
                          } else if (dailyQuestionAnswer?.memory_id) {
                            navigate(`/my-life/memories/${dailyQuestionAnswer.memory_id}`);
                          }
                        }}
                        style={{
                          padding: "10px 22px", borderRadius: 12, border: "2px solid rgba(255,255,255,0.3)",
                          background: "transparent", color: "#fff", cursor: "pointer",
                          fontSize: 14, fontWeight: 700, fontFamily: FONT_BODY,
                          display: "flex", alignItems: "center", gap: 8,
                          transition: "background 0.2s, border-color 0.2s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; }}
                      >
                        {"\u270F\uFE0F"} Edit Story
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/tell-a-story')}
                      style={{
                        padding: "12px 26px", borderRadius: 14, border: "none",
                        background: "#E65100", color: "#fff", cursor: "pointer",
                        fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                        display: "flex", alignItems: "center", gap: 8,
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                    >
                      {"\u270D\uFE0F"} Tell a Story
                    </button>
                  )}
                </div>

                {/* Upcoming Capsules */}
                <div style={{
                  background: "#fff", borderRadius: 20, border: "1px solid #E8E4DA",
                  padding: 26, marginBottom: 22, boxShadow: "0 1px 6px rgba(0,0,0,0.03)",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: 18,
                  }}>
                    <h3 style={{
                      fontSize: 18, fontWeight: 800, color: "#2C3E1A", margin: 0,
                      fontFamily: FONT_HEADING,
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      {"\u{1F4EC}"} Upcoming Capsules
                    </h3>
                    <button onClick={() => navigate('/timecapsule')} style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 14, fontWeight: 700, color: "#556B2F",
                      fontFamily: FONT_BODY,
                    }}>
                      View All &rarr;
                    </button>
                  </div>
                  {upcomingCapsules.length > 0 ? upcomingCapsules.map((cap, i) => (
                    <div key={cap.id} style={{
                      padding: "16px 18px", borderRadius: 14, background: "#F8F7F2",
                      marginBottom: i < upcomingCapsules.length - 1 ? 10 : 0,
                      border: "1px solid #EDEADF",
                    }}>
                      <h4 style={{
                        fontSize: 16, fontWeight: 700, color: "#333", margin: "0 0 6px",
                        fontFamily: FONT_BODY,
                      }}>
                        {cap.title}
                      </h4>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        fontSize: 14, color: "#1565C0", fontFamily: FONT_BODY,
                      }}>
                        <CalendarIcon />
                        Delivers {cap.deliveryDate?.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                      </div>
                    </div>
                  )) : (
                    <div style={{ textAlign: "center", padding: "20px 10px", color: "#999" }}>
                      <p style={{ fontSize: 14, fontFamily: FONT_BODY }}>No upcoming capsules</p>
                    </div>
                  )}
                </div>


              </div>
            </div>
          </div>
        </div>
      )}
    </HubLayout>
  );
}

// --- Helper Functions ---

function formatRecipients(recipients) {
  if (!recipients) return 'Unknown';
  if (typeof recipients === 'string') return recipients;
  if (Array.isArray(recipients)) {
    if (recipients.length === 0) return 'Unknown';
    if (recipients.length === 1) return recipients[0].name || recipients[0];
    return `${recipients[0].name || recipients[0]} +${recipients.length - 1} more`;
  }
  return recipients.name || 'Unknown';
}

function getCapsuleStatus(capsule) {
  if (capsule.is_delivered || capsule.delivered) return 'Delivered';
  if (capsule.is_scheduled || capsule.delivery_date) return 'Scheduled';
  return 'Draft';
}

function getMilestoneStatus(milestone) {
  const now = new Date();
  let milestoneDate;
  if (milestone.year) {
    milestoneDate = new Date(milestone.year, milestone.month ? milestone.month - 1 : 11, 28);
  } else if (milestone.date) {
    milestoneDate = new Date(milestone.date);
  } else {
    return 'upcoming';
  }
  if (milestoneDate > now) return 'upcoming';
  if (milestone.has_capsule || milestone.linked_capsule) return 'active';
  return 'completed';
}

function getMilestoneStatusLabel(milestone) {
  const status = getMilestoneStatus(milestone);
  switch (status) {
    case 'upcoming': return 'Coming up';
    case 'active': return 'Capsule Linked';
    case 'completed': return 'Lived \u2713';
    default: return '';
  }
}

export default Hub;
